import type { Customer } from '../api/active/generated/customer';
import { randomUuid } from '../api/active/randomUuid';
import {
  assertFragmentAllocation,
  assertSourceFragment,
  usesQuoteFragmentAllocation,
} from '../domain/quoteSourceFragment';
import { defaultQuoteDiscountForBusinessMode } from '../pages/cpq-v2/state/businessModes';
import { readOutputTermsDisplayOverrides } from '../pages/cpq-v2/state/outputTerms';
import { quoteDeviceCustomFields } from '../pages/cpq-v2/state/quoteDeviceCustomFields';
import { isMaskedLocalPlanPriceContextVersion } from '../pages/cpq-v2/state/quoteLocalPlanPriceVisibility';
import { readQuoteModeDiscounts } from '../pages/cpq-v2/state/quoteModeDiscounts';
import { quoteTableGroupKey } from '../pages/cpq-v2/state/quoteTableGroups';
import {
  occurrenceSourceLineCents,
  planProductPrice,
  planProductSubtotal,
} from '../pages/cpq-v2/state/quoteTotals';
import type {
  BusinessModeCode,
  CpqState,
  CustomerCatalogRecord,
  DeviceCatalogRecord,
  OriginalQuoteDeviceIdentity,
  PackageCatalogRecord,
  PlanCatalogRecord,
  Quote,
  QuoteContainerInstance,
  QuoteItem,
  QuoteLocalPlanInstance,
} from '../pages/cpq-v2/state/types';
import {
  type QuoteGroupModel,
  type QuoteManagementRecordModel,
  type QuoteWorkspaceDraftItem,
  type QuoteWorkspaceModel,
  quoteWorkspaceBusinessModeCode,
} from './quoteMapper';
import { readReplacementTargetMedia } from './quoteReplacementMedia';
import { quoteExpiryDate } from './quoteShareForm';

const yuan = (cents: number | null | undefined) => (cents ?? 0) / 100;

function customizedLocalPlanRootIds(
  instances: readonly QuoteLocalPlanInstance[] | undefined,
  containers: readonly QuoteContainerInstance[] | undefined,
): Set<string> {
  return new Set(
    (containers ?? [])
      .filter(
        (container) =>
          container.kind === 'plan' &&
          container.parentInstanceId === null &&
          container.parentMemberId === null &&
          container.originKind === 'quote-local-plan' &&
          container.status === 'customized' &&
          (instances ?? []).some(
            (instance) =>
              instance.id === container.id &&
              instance.status === 'active' &&
              instance.definitionId === container.originId &&
              instance.definitionResourceVersion ===
                (container.originResourceVersion ??
                  container.originSnapshot.resourceVersion),
          ),
      )
      .map((container) => container.id),
  );
}

export type FormalOrderQuoteState =
  | 'draft'
  | 'collaboration'
  | 'pending_submission'
  | 'submitted';

export const FORMAL_NEW_QUOTE_ID = 'FORMAL-NEW';

export function isFormalNewQuoteId(quoteId: string): boolean {
  return (
    quoteId === FORMAL_NEW_QUOTE_ID || /^FORMAL-NEW-[1-9][0-9]*$/.test(quoteId)
  );
}

export function nextFormalNewQuoteId(current: CpqState): string {
  if (!current.openQuoteIds.includes(FORMAL_NEW_QUOTE_ID))
    return FORMAL_NEW_QUOTE_ID;
  const used = new Set(current.openQuoteIds);
  let sequence = 2;
  while (used.has(`${FORMAL_NEW_QUOTE_ID}-${sequence}`)) sequence += 1;
  return `${FORMAL_NEW_QUOTE_ID}-${sequence}`;
}

export function formalQuoteTabs(current: CpqState, quoteId: string): string[] {
  const existing = current.openQuoteIds.filter(
    (id) =>
      /^[1-9][0-9]{0,18}$/.test(id) ||
      (isFormalNewQuoteId(id) && Boolean(current.quotes[id])),
  );
  return existing.includes(quoteId) ? existing : [...existing, quoteId];
}

export function projectCustomer(customer: Customer): CustomerCatalogRecord {
  const primary = customer.contacts.find((contact) => contact.primary);
  return {
    id: customer.id,
    customerNo: customer.customerNo,
    name: customer.name,
    contact: primary?.name ?? '',
    phone: primary?.phone ?? '',
    manager: customer.primaryOwnerName ?? '',
    managerUserId: customer.primaryOwnerUserId,
    status: customer.status,
  };
}

function legacySourceType(sourceType: QuoteWorkspaceDraftItem['sourceType']) {
  return sourceType === 'equipment' ? ('device' as const) : sourceType;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter(
        (entry): entry is string =>
          typeof entry === 'string' && entry.length > 0,
      )
    : [];
}

function numericRecord(value: unknown): Record<string, number> | undefined {
  if (value === null || typeof value !== 'object' || Array.isArray(value))
    return undefined;
  const entries = Object.entries(value).filter(
    (entry): entry is [string, number] =>
      typeof entry[1] === 'number' && Number.isFinite(entry[1]),
  );
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function originalDeviceIdentity(
  item: QuoteWorkspaceModel['items'][number],
): OriginalQuoteDeviceIdentity | null {
  const raw = item.sourceSnapshot.originalDeviceIdentity;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const identity = raw as Record<string, unknown>;
  if (
    identity.deviceId !== item.deviceId ||
    typeof identity.deviceRevisionId !== 'string' ||
    !/^[1-9][0-9]{0,18}$/.test(identity.deviceRevisionId) ||
    (item.sourceType === 'equipment' &&
      identity.deviceRevisionId !== item.sourceRevisionId) ||
    typeof identity.name !== 'string' ||
    !identity.name ||
    (identity.sku !== null && typeof identity.sku !== 'string') ||
    (identity.jkyCode !== null && typeof identity.jkyCode !== 'string')
  )
    return null;
  return {
    deviceId: item.deviceId,
    deviceRevisionId: identity.deviceRevisionId,
    name: identity.name,
    sku: identity.sku,
    jkyCode: identity.jkyCode,
  };
}

export function projectWorkspaceItem(
  item: QuoteWorkspaceModel['items'][number],
  businessModeCode: BusinessModeCode = 'buyout',
): QuoteItem {
  const fragment = item.sourceSnapshot.sourceFragment;
  if (fragment !== undefined) assertSourceFragment(fragment);
  const fragmentAllocation = item.pricingSnapshot?.fragmentAllocation;
  if (fragment && usesQuoteFragmentAllocation(fragment))
    assertFragmentAllocation(fragmentAllocation, fragment, item.quantity);
  const snapshot = item.deviceFieldSnapshot;
  const sourceIdentity = originalDeviceIdentity(item);
  const numeric = (key: string): number | null =>
    typeof snapshot[key] === 'number' && Number.isFinite(snapshot[key])
      ? snapshot[key]
      : null;
  const planProductPrice =
    item.planProductPriceSnapshotCents === null
      ? undefined
      : yuan(item.planProductPriceSnapshotCents);
  const outputPriceFacts = item.pricingSnapshot?.outputPriceFacts;
  const rentalFacts =
    outputPriceFacts && typeof outputPriceFacts === 'object'
      ? (outputPriceFacts as Record<string, unknown>)
      : undefined;
  const quoteLocalPackageUnitPriceCents =
    Number.isSafeInteger(rentalFacts?.quoteLocalPackageUnitPriceCents) &&
    (rentalFacts?.quoteLocalPackageUnitPriceCents as number) >= 0
      ? (rentalFacts?.quoteLocalPackageUnitPriceCents as number)
      : undefined;
  const exactSourceLineCents =
    item.pricingSnapshot?.purchasePriceBasisVersion === 'tax13-v1'
      ? rentalFacts?.rentalBasisLineCents
      : item.pricingSnapshot?.sourceLineCents;
  if (
    item.sourceType === 'plan' &&
    item.pricingSnapshot?.purchasePriceBasisVersion === 'tax13-v1' &&
    (!Number.isSafeInteger(exactSourceLineCents) ||
      (exactSourceLineCents as number) < 0)
  )
    throw new Error('购买方案行缺少精确租赁来源金额');
  const planProductSubtotal =
    item.sourceType === 'plan' &&
    typeof exactSourceLineCents === 'number' &&
    Number.isSafeInteger(exactSourceLineCents)
      ? yuan(exactSourceLineCents)
      : planProductPrice === undefined ||
          item.packageRelationQuantitySnapshot === null
        ? undefined
        : planProductPrice * item.packageRelationQuantitySnapshot;
  const replacementSelection =
    item.pricingSnapshot?.replacementSelection !== null &&
    typeof item.pricingSnapshot?.replacementSelection === 'object' &&
    !Array.isArray(item.pricingSnapshot.replacementSelection)
      ? (item.pricingSnapshot.replacementSelection as Record<string, unknown>)
      : undefined;
  const replacementSnapshot =
    replacementSelection?.replacementDeviceSnapshot !== null &&
    typeof replacementSelection?.replacementDeviceSnapshot === 'object' &&
    !Array.isArray(replacementSelection.replacementDeviceSnapshot)
      ? (replacementSelection.replacementDeviceSnapshot as Record<
          string,
          unknown
        >)
      : undefined;
  const originalPriceSnapshot: QuoteItem['originalDevicePriceSnapshot'] =
    sourceIdentity
      ? { deviceRevisionId: sourceIdentity.deviceRevisionId }
      : undefined;
  const rawIdentity = item.sourceSnapshot.originalDeviceIdentity as Record<
    string,
    unknown
  > | null;
  if (originalPriceSnapshot) {
    for (const key of [
      'retailPriceTax13',
      'taxInclusivePlanPrice',
      'untaxedPlanPrice',
    ] as const) {
      const cents = rawIdentity?.[`${key}Cents`];
      if (rawIdentity && Object.hasOwn(rawIdentity, `${key}Cents`)) {
        if (
          cents === null ||
          (typeof cents === 'number' &&
            Number.isSafeInteger(cents) &&
            cents >= 0)
        )
          originalPriceSnapshot[key] = cents === null ? null : cents / 100;
      } else if (
        !replacementSelection &&
        !item.replacementRelationId &&
        Object.hasOwn(snapshot, key)
      ) {
        const value = snapshot[key];
        if (
          value === null ||
          (typeof value === 'number' && Number.isFinite(value) && value >= 0)
        )
          originalPriceSnapshot[key] = value;
      }
    }
  }
  // A missing target key retains the old effective snapshot fallback. Exact
  // source identity is displayed only after the replacement is cleared.
  const displaySourceIdentity =
    replacementSelection || item.replacementRelationId
      ? undefined
      : sourceIdentity;
  const replacementAmount = (key: string, original: number | null) => {
    // 平替目标明确未知的金额不能回退到原设备金额。
    if (
      businessModeCode !== 'buyout' ||
      !replacementSnapshot ||
      !Object.hasOwn(replacementSnapshot, key)
    )
      return original;
    const amount = replacementSnapshot[key];
    return typeof amount === 'number' && Number.isFinite(amount)
      ? amount / 100
      : null;
  };
  const referenceAmount = (key: string, original: number | null) => {
    if (!rentalFacts || !Object.hasOwn(rentalFacts, key)) {
      if (
        ![
          'retailPriceTax13Cents',
          'taxInclusivePlanPriceCents',
          'untaxedPlanPriceCents',
        ].includes(key)
      )
        return replacementAmount(key, original);
      if (!replacementSnapshot || !Object.hasOwn(replacementSnapshot, key))
        return original;
      const amount = replacementSnapshot[key];
      return typeof amount === 'number' &&
        Number.isSafeInteger(amount) &&
        amount >= 0
        ? amount / 100
        : null;
    }
    const amount = rentalFacts[key];
    return rentalFacts.kind === 'quote-output-price-facts-v1' &&
      typeof amount === 'number' &&
      Number.isSafeInteger(amount) &&
      amount >= 0
      ? amount / 100
      : null;
  };
  const effectiveCostAmount =
    replacementSelection || item.replacementRelationId
      ? referenceAmount
      : replacementAmount;
  const replacementName =
    typeof replacementSnapshot?.name === 'string'
      ? replacementSnapshot.name
      : undefined;
  const replacementSku =
    typeof replacementSnapshot?.sku === 'string'
      ? replacementSnapshot.sku
      : replacementSnapshot?.sku === null
        ? null
        : undefined;
  const replacementJkyCode =
    typeof replacementSnapshot?.jkyCode === 'string'
      ? replacementSnapshot.jkyCode
      : replacementSnapshot?.jkyCode === null
        ? null
        : undefined;
  const localPricingRaw =
    snapshot.quoteLocalPricing !== null &&
    typeof snapshot.quoteLocalPricing === 'object' &&
    !Array.isArray(snapshot.quoteLocalPricing)
      ? (snapshot.quoteLocalPricing as Record<string, unknown>)
      : undefined;
  const quoteLocalPricing = localPricingRaw
    ? {
        ...(typeof localPricingRaw.dynamicCostCents === 'number'
          ? { dynamicCostCents: localPricingRaw.dynamicCostCents }
          : {}),
        ...(typeof localPricingRaw.dynamicCostLineCents === 'number'
          ? { dynamicCostLineCents: localPricingRaw.dynamicCostLineCents }
          : {}),
        ...(typeof localPricingRaw.rentalPriceCents === 'number'
          ? { rentalPriceCents: localPricingRaw.rentalPriceCents }
          : {}),
        ...(typeof localPricingRaw.retailPriceTax13Cents === 'number'
          ? { retailPriceTax13Cents: localPricingRaw.retailPriceTax13Cents }
          : {}),
        ...(typeof localPricingRaw.exclusiveDiscountPercent === 'string'
          ? {
              exclusiveDiscountPercent:
                localPricingRaw.exclusiveDiscountPercent,
            }
          : {}),
        ...(typeof localPricingRaw.exclusiveLineCents === 'number'
          ? { exclusiveLineCents: localPricingRaw.exclusiveLineCents }
          : {}),
        ...(typeof localPricingRaw.exclusiveLineBasisCents === 'number'
          ? { exclusiveLineBasisCents: localPricingRaw.exclusiveLineBasisCents }
          : {}),
        ...(typeof localPricingRaw.exclusiveLineBasisQuantity === 'number'
          ? {
              exclusiveLineBasisQuantity:
                localPricingRaw.exclusiveLineBasisQuantity,
            }
          : {}),
      }
    : undefined;
  return {
    itemId: item.id,
    ...(fragment
      ? {
          sourceFragment: structuredClone(fragment),
          fragmentAllocation: structuredClone(
            fragmentAllocation,
          ) as QuoteItem['fragmentAllocation'],
        }
      : {}),
    formalLineTotalCents: item.lineTotalCents,
    ...(Number.isSafeInteger(rentalFacts?.rentalLineTotalCents)
      ? {
          formalRentalLineTotalCents:
            rentalFacts?.rentalLineTotalCents as number,
        }
      : {}),
    ...(item.groupId ? { quoteGroupId: item.groupId } : {}),
    ...(item.containerInstanceId
      ? { containerInstanceId: item.containerInstanceId }
      : {}),
    sourceRevisionId: item.sourceRevisionId,
    // Preserve the exact package revision carried by plan rows. Without this
    // bridge, reopening a draft/splitting a plan drops the duplicate package
    // identity and the save/share/order paths can only guess by display name.
    packageRevisionId:
      typeof item.sourceSnapshot.packageRevisionId === 'string'
        ? item.sourceSnapshot.packageRevisionId
        : item.sourceType === 'package'
          ? item.sourceRevisionId
          : undefined,
    packageNameSnapshot:
      typeof item.packageNameSnapshot === 'string'
        ? item.packageNameSnapshot
        : typeof item.sourceSnapshot.packageNameSnapshot === 'string'
          ? item.sourceSnapshot.packageNameSnapshot
          : undefined,
    packageOriginPlanId:
      typeof item.sourceSnapshot.packageOrigin === 'object' &&
      item.sourceSnapshot.packageOrigin !== null &&
      'planId' in item.sourceSnapshot.packageOrigin &&
      typeof item.sourceSnapshot.packageOrigin.planId === 'string'
        ? item.sourceSnapshot.packageOrigin.planId
        : undefined,
    packageOriginPlanRevisionId:
      typeof item.sourceSnapshot.packageOrigin === 'object' &&
      item.sourceSnapshot.packageOrigin !== null &&
      'planRevisionId' in item.sourceSnapshot.packageOrigin &&
      typeof item.sourceSnapshot.packageOrigin.planRevisionId === 'string'
        ? item.sourceSnapshot.packageOrigin.planRevisionId
        : undefined,
    planPackageMembershipId:
      typeof item.sourceSnapshot.planPackageMembershipId === 'string'
        ? item.sourceSnapshot.planPackageMembershipId
        : undefined,
    sourceCode:
      typeof item.sourceSnapshot.code === 'string'
        ? item.sourceSnapshot.code
        : undefined,
    deviceId: item.deviceId,
    originalDeviceIdentity: sourceIdentity,
    ...(originalPriceSnapshot && Object.keys(originalPriceSnapshot).length > 1
      ? { originalDevicePriceSnapshot: originalPriceSnapshot }
      : {}),
    ...(sourceIdentity &&
    (snapshot.dynamicCost === null ||
      (typeof snapshot.dynamicCost === 'number' &&
        Number.isFinite(snapshot.dynamicCost)))
      ? {
          originalDeviceCostSnapshot: {
            deviceRevisionId: sourceIdentity.deviceRevisionId,
            dynamicCost: snapshot.dynamicCost,
          },
        }
      : {}),
    originalDeviceMedia:
      sourceIdentity &&
      snapshot.deviceRevisionId === sourceIdentity.deviceRevisionId
        ? {
            imageObjectIds: stringList(snapshot.imageObjectIds),
            ...(typeof snapshot.primaryImageObjectId === 'string'
              ? { primaryImageObjectId: snapshot.primaryImageObjectId }
              : {}),
          }
        : null,
    sourceType: legacySourceType(item.sourceType),
    sourceId: item.sourceId,
    sourceName: item.sourceSnapshot.name,
    formalPlanOccurrence: item.sourceSnapshot
      .planOccurrence as QuoteItem['formalPlanOccurrence'],
    name:
      replacementName ??
      displaySourceIdentity?.name ??
      (typeof snapshot.name === 'string' ? snapshot.name : item.nameSnapshot),
    sku:
      replacementSku !== undefined
        ? (replacementSku ?? '')
        : displaySourceIdentity
          ? (displaySourceIdentity.sku ?? '')
          : typeof snapshot.sku === 'string'
            ? snapshot.sku
            : '',
    quantity: item.quantity,
    unitPrice: yuan(item.unitPriceCents),
    discount: Number(item.discountPercent),
    pricingSnapshot: { ...item.pricingSnapshot },
    modeDiscounts: readQuoteModeDiscounts(item.pricingSnapshot?.modeDiscounts),
    pricingBaseSnapshot: {
      kind:
        item.pricingSnapshot?.purchasePriceBasisVersion === 'tax13-v1'
          ? 'buyout-tax13'
          : quoteLocalPackageUnitPriceCents !== undefined
            ? 'package-item'
            : item.sourceType === 'equipment'
              ? 'retail-tax13'
              : 'package-item',
      amount:
        item.pricingSnapshot?.purchasePriceBasisVersion === 'tax13-v1'
          ? yuan(item.unitPriceCents)
          : quoteLocalPackageUnitPriceCents !== undefined
            ? yuan(quoteLocalPackageUnitPriceCents)
            : item.sourceType === 'equipment' ||
                item.packageItemPriceSnapshotCents === null
              ? yuan(item.unitPriceCents)
              : yuan(item.packageItemPriceSnapshotCents),
      calculationVersion: 'pricing-v2',
    },
    packageRelationId: item.packageRelationId ?? undefined,
    packageId: item.packageId ?? undefined,
    packageItemPriceSnapshot:
      quoteLocalPackageUnitPriceCents !== undefined
        ? yuan(quoteLocalPackageUnitPriceCents)
        : item.packageItemPriceSnapshotCents === null
          ? undefined
          : yuan(item.packageItemPriceSnapshotCents),
    ...(item.packageRelationId &&
    Number.isSafeInteger(rentalFacts?.packageOriginalLineCents) &&
    (rentalFacts?.packageOriginalLineCents as number) >= 0 &&
    item.packageRelationQuantitySnapshot != null &&
    item.packageRelationQuantitySnapshot > 0 &&
    item.quantity % item.packageRelationQuantitySnapshot === 0
      ? {
          packageItemMonthlySubtotalSnapshot:
            yuan(rentalFacts?.packageOriginalLineCents as number) /
            (item.quantity / item.packageRelationQuantitySnapshot),
        }
      : {}),
    packageRelationQuantitySnapshot:
      item.packageRelationQuantitySnapshot ?? undefined,
    planId: item.planId ?? undefined,
    planDiscountSnapshot:
      item.planDiscountSnapshot === null
        ? undefined
        : Number(item.planDiscountSnapshot),
    planProductPriceSnapshot: planProductPrice,
    planProductSubtotalSnapshot: planProductSubtotal,
    replacementPoolId:
      typeof item.sourceSnapshot.replacementPoolId === 'string'
        ? item.sourceSnapshot.replacementPoolId
        : typeof replacementSelection?.poolId === 'string'
          ? replacementSelection.poolId
          : undefined,
    replacementRelationId: item.replacementRelationId ?? undefined,
    replacementDeviceId:
      typeof replacementSelection?.replacementDeviceId === 'string'
        ? replacementSelection.replacementDeviceId
        : undefined,
    replacementTargetMedia: readReplacementTargetMedia(
      replacementSelection?.targetMedia,
      replacementSelection?.replacementDeviceId,
    ),
    formalCustomFieldPreviews:
      snapshot.customFieldPreviews &&
      typeof snapshot.customFieldPreviews === 'object'
        ? Object.fromEntries(
            quoteDeviceCustomFields(snapshot.customFields).flatMap((field) => {
              const url = (
                snapshot.customFieldPreviews as Record<string, unknown>
              )[field.definitionId];
              return field.value &&
                typeof field.value === 'object' &&
                typeof url === 'string'
                ? [
                    [
                      field.definitionId,
                      { storedObjectId: field.value.storedObjectId, url },
                    ],
                  ]
                : [];
            }),
          )
        : undefined,
    deviceFieldSnapshot: {
      customFields: quoteDeviceCustomFields(snapshot.customFields),
      customPriceFields: Array.isArray(snapshot.customPriceFields)
        ? snapshot.customPriceFields.filter(
            (
              field,
            ): field is {
              definitionId: string;
              definitionVersion: number;
              value: number | null;
            } =>
              typeof field === 'object' &&
              field !== null &&
              typeof field.definitionId === 'string' &&
              typeof field.definitionVersion === 'number' &&
              (field.value === null || typeof field.value === 'number'),
          )
        : undefined,
      name:
        replacementName ??
        displaySourceIdentity?.name ??
        (typeof snapshot.name === 'string' ? snapshot.name : undefined),
      sku:
        replacementSku !== undefined
          ? replacementSku
          : displaySourceIdentity
            ? displaySourceIdentity.sku
            : typeof snapshot.sku === 'string'
              ? snapshot.sku
              : undefined,
      equipmentCode:
        typeof snapshot.equipmentCode === 'string'
          ? snapshot.equipmentCode
          : undefined,
      jkyCode:
        replacementJkyCode !== undefined
          ? replacementJkyCode
          : displaySourceIdentity
            ? displaySourceIdentity.jkyCode
            : typeof snapshot.jkyCode === 'string'
              ? snapshot.jkyCode
              : '',
      rentalPrice: referenceAmount('rentalPriceCents', numeric('rentalPrice')),
      ...(snapshot.shortRentalPriceCents === null ||
      (typeof snapshot.shortRentalPriceCents === 'number' &&
        Number.isSafeInteger(snapshot.shortRentalPriceCents) &&
        snapshot.shortRentalPriceCents >= 0)
        ? { shortRentalPriceCents: snapshot.shortRentalPriceCents }
        : {}),
      taxInclusivePlanPrice: referenceAmount(
        'taxInclusivePlanPriceCents',
        numeric('taxInclusivePlanPrice'),
      ),
      unitPrice: replacementAmount('unitPriceCents', numeric('unitPrice')),
      untaxedPlanPrice: referenceAmount(
        'untaxedPlanPriceCents',
        numeric('untaxedPlanPrice'),
      ),
      retailPriceTax13: referenceAmount(
        'retailPriceTax13Cents',
        numeric('retailPriceTax13'),
      ),
      buyoutFinalPriceTax13:
        typeof snapshot.buyoutFinalPriceTax13 === 'number'
          ? snapshot.buyoutFinalPriceTax13
          : Number.NaN,
      ...('purchaseFinalPriceExTax13' in snapshot
        ? {
            purchaseFinalPriceExTax13:
              typeof snapshot.purchaseFinalPriceExTax13 === 'number'
                ? snapshot.purchaseFinalPriceExTax13
                : Number.NaN,
          }
        : {}),
      dynamicCost: effectiveCostAmount(
        'dynamicCostCents',
        snapshot.dynamicCost === null
          ? null
          : typeof snapshot.dynamicCost === 'number' &&
              Number.isFinite(snapshot.dynamicCost)
            ? snapshot.dynamicCost
            : Number.NaN,
      ),
      ...(quoteLocalPricing ? { quoteLocalPricing } : {}),
      compensationPrice: replacementAmount(
        'compensationPriceCents',
        numeric('compensationPrice'),
      ),
      deviceRevisionId:
        typeof replacementSelection?.replacementDeviceRevisionId === 'string'
          ? replacementSelection.replacementDeviceRevisionId
          : (displaySourceIdentity?.deviceRevisionId ??
            (typeof snapshot.deviceRevisionId === 'string'
              ? snapshot.deviceRevisionId
              : undefined)),
      imageObjectIds: stringList(snapshot.imageObjectIds),
      primaryImageObjectId:
        typeof snapshot.primaryImageObjectId === 'string'
          ? snapshot.primaryImageObjectId
          : undefined,
      category:
        typeof snapshot.category === 'string' ? snapshot.category : undefined,
      deviceAttribute:
        typeof snapshot.deviceAttribute === 'string'
          ? snapshot.deviceAttribute
          : undefined,
      procurementAttribute:
        typeof snapshot.procurementAttribute === 'string'
          ? snapshot.procurementAttribute
          : undefined,
      procurementCycle:
        typeof snapshot.procurementCycle === 'string'
          ? snapshot.procurementCycle
          : undefined,
      weight:
        snapshot.weight === null
          ? null
          : typeof snapshot.weight === 'number' &&
              Number.isFinite(snapshot.weight)
            ? snapshot.weight
            : undefined,
      remark: typeof snapshot.remark === 'string' ? snapshot.remark : undefined,
      localPoint:
        typeof snapshot.localPoint === 'string'
          ? snapshot.localPoint
          : undefined,
      localPrice:
        typeof snapshot.localPrice === 'string'
          ? snapshot.localPrice
          : undefined,
      offsitePoint:
        typeof snapshot.offsitePoint === 'string'
          ? snapshot.offsitePoint
          : undefined,
      offsitePrice:
        typeof snapshot.offsitePrice === 'string'
          ? snapshot.offsitePrice
          : undefined,
      stockByWarehouse: numericRecord(snapshot.stockByWarehouse),
    },
    stock: 0,
    remark: item.remark ?? undefined,
  };
}

type DeviceFactSource = Pick<
  DeviceCatalogRecord,
  | 'id'
  | 'deviceAttribute'
  | 'procurementAttribute'
  | 'procurementCycle'
  | 'weight'
  | 'remark'
  | 'localPoint'
  | 'localPrice'
  | 'offsitePoint'
  | 'offsitePrice'
  | 'stockByWarehouse'
>;

export function restoreMissingDeviceFacts(
  item: QuoteItem,
  devices: readonly DeviceFactSource[],
): QuoteItem {
  const device = devices.find((candidate) => candidate.id === item.deviceId);
  if (!device) return item;
  const snapshot: NonNullable<QuoteItem['deviceFieldSnapshot']> =
    item.deviceFieldSnapshot ?? {
      jkyCode: '',
      retailPriceTax13: null,
      dynamicCost: null,
    };
  // Migrated quote revisions predate the descriptive-field snapshot contract.
  // Preserve every frozen value first, then fill only absent non-pricing facts
  // from the explicitly bound device id. Price facts always remain frozen.
  return {
    ...item,
    deviceFieldSnapshot: {
      ...snapshot,
      deviceAttribute: snapshot.deviceAttribute ?? device.deviceAttribute,
      procurementAttribute:
        snapshot.procurementAttribute ?? device.procurementAttribute,
      procurementCycle: snapshot.procurementCycle ?? device.procurementCycle,
      weight: snapshot.weight === undefined ? device.weight : snapshot.weight,
      remark: snapshot.remark ?? device.remark,
      localPoint: snapshot.localPoint ?? device.localPoint,
      localPrice: snapshot.localPrice ?? device.localPrice,
      offsitePoint: snapshot.offsitePoint ?? device.offsitePoint,
      offsitePrice: snapshot.offsitePrice ?? device.offsitePrice,
      stockByWarehouse:
        snapshot.stockByWarehouse ?? structuredClone(device.stockByWarehouse),
    },
  };
}

export function alignDraftPlanPricingWithCatalog(
  quote: Quote,
  plans: PlanCatalogRecord[],
): Quote {
  if (quote.status !== 'draft') return quote;
  return {
    ...quote,
    items: quote.items.map((item) => {
      if (item.sourceFragment) return item;
      if (!item.planId || item.packageItemPriceSnapshot === undefined)
        return item;
      const exactPlan = plans.find(
        (plan) =>
          plan.id === item.planId &&
          plan.currentRevisionId === item.sourceRevisionId,
      );
      // A permitted quote does not require readable source-price fields.
      if (exactPlan && exactPlan.formalDiscount === undefined) return item;
      const matchingPackages = exactPlan?.formalPackages?.filter(
        (pkg) =>
          pkg.packageId === item.packageId &&
          (!item.planPackageMembershipId ||
            pkg.planPackageMembershipId === item.planPackageMembershipId) &&
          (!item.packageRevisionId ||
            pkg.packageRevisionId === item.packageRevisionId),
      );
      const authoritativePackage =
        matchingPackages?.length === 1 ? matchingPackages[0] : undefined;
      const authoritativeRelation = authoritativePackage?.items.find(
        (relation) =>
          relation.packageRelationId === item.packageRelationId &&
          relation.deviceId === item.deviceId,
      );
      const packageQuantity = authoritativeRelation
        ? item.quantity / authoritativeRelation.quantity
        : NaN;
      if (
        !exactPlan?.occurrenceModel &&
        exactPlan?.status === 'active' &&
        !exactPlan.incompleteReasons?.length &&
        authoritativePackage &&
        authoritativeRelation &&
        authoritativeRelation.lineMonthlySubtotalCents !== null &&
        Number.isSafeInteger(packageQuantity) &&
        packageQuantity >= 1 &&
        authoritativeRelation.quantity ===
          item.packageRelationQuantitySnapshot &&
        authoritativeRelation.packageItemMonthlyPriceCents ===
          Math.round(item.packageItemPriceSnapshot * 100)
      ) {
        const lineCents = authoritativeRelation.lineMonthlySubtotalCents;
        const quantity = authoritativeRelation.quantity;
        return {
          ...item,
          formalPlanOccurrence: undefined,
          planDiscountSnapshot:
            exactPlan.formalDiscount === null
              ? undefined
              : exactPlan.formalDiscount,
          planProductPriceSnapshot:
            Math.floor((lineCents + Math.floor(quantity / 2)) / quantity) / 100,
          planProductSubtotalSnapshot: (lineCents / 100) * packageQuantity,
        };
      }
      if (item.formalPlanOccurrence) {
        const tuple = item.formalPlanOccurrence;
        const model = exactPlan?.occurrenceModel;
        const occurrence = model?.occurrences.find(
          (candidate) => candidate.occurrenceKey === tuple.occurrenceKey,
        );
        const line = occurrence?.items.find(
          (candidate) => candidate.itemKey === tuple.itemKey,
        );
        const frozenPackagePriceCents = Math.round(
          item.packageItemPriceSnapshot * 100,
        );
        if (
          exactPlan?.status !== 'active' ||
          exactPlan.incompleteReasons?.length ||
          model?.targetStatus !== 'active' ||
          model.pricingCoefficient !== tuple.pricingCoefficient ||
          model.rounding !== tuple.rounding ||
          !occurrence ||
          occurrence.packageId !== item.packageId ||
          occurrence.packageRevisionId !== tuple.packageRevisionId ||
          !line ||
          item.sourceType !== 'plan' ||
          item.sourceId !== exactPlan.id ||
          line.deviceId !== item.deviceId ||
          line.deviceRevisionId !== tuple.deviceRevisionId ||
          line.monthlyPriceCents !== frozenPackagePriceCents ||
          !Number.isSafeInteger(item.quantity / (line.quantity ?? 0)) ||
          item.quantity < (line.quantity ?? 0) ||
          line.quantity !== item.packageRelationQuantitySnapshot
        )
          return item;
        const planProductPrice =
          occurrenceSourceLineCents({
            formalPlanOccurrence: tuple,
            packageItemPriceSnapshot: item.packageItemPriceSnapshot,
            quantity: 1,
          }) / 100;
        const quantity = item.quantity;
        return {
          ...item,
          planDiscountSnapshot: undefined,
          planProductPriceSnapshot: planProductPrice,
          planProductSubtotalSnapshot: planProductPrice * quantity,
          formalPlanOccurrence: {
            ...item.formalPlanOccurrence,
            sourceLineCents: Math.round(planProductPrice * 100) * quantity,
          },
        };
      }
      if (exactPlan?.occurrenceModel || exactPlan?.formalDiscount === null)
        return item;
      const discount = item.planDiscountSnapshot ?? exactPlan?.formalDiscount;
      if (discount === undefined) return item;
      return {
        ...item,
        planDiscountSnapshot: discount,
        planProductPriceSnapshot: planProductPrice(
          item.packageItemPriceSnapshot,
          discount,
        ),
        planProductSubtotalSnapshot: planProductSubtotal(
          item.packageItemPriceSnapshot,
          discount,
          item.packageRelationQuantitySnapshot ?? item.quantity,
        ),
      };
    }),
  };
}

export function projectWorkspaceState(
  current: CpqState,
  workspace: QuoteWorkspaceModel,
  customers: CustomerCatalogRecord[],
  devices: DeviceCatalogRecord[],
  packages: PackageCatalogRecord[],
  plans: PlanCatalogRecord[],
  orderQuoteState?: FormalOrderQuoteState,
  onProjectedQuote?: (quote: Quote) => void,
): CpqState {
  const template =
    current.quotes[workspace.id] ?? Object.values(current.quotes)[0];
  const previousDraft = current.quotes[workspace.id];
  const selectedCustomerId = workspace.fixedShareLinkId
    ? workspace.customerId
    : previousDraft?.customer.customerId || workspace.customerId;
  const businessModeCode = quoteWorkspaceBusinessModeCode(workspace);
  const customizedPlanRoots = customizedLocalPlanRootIds(
    workspace.localPlanInstances,
    workspace.selectedVersion.containerInstances,
  );
  const containerInstancesById = new Map(
    (workspace.selectedVersion.containerInstances ?? []).map((container) => [
      container.id,
      container,
    ]),
  );
  const quote: Quote = {
    ...template,
    quoteId: workspace.id,
    masterId: workspace.id,
    version: Number(workspace.selectedVersion.versionNo),
    title: workspace.quoteNo,
    validUntil: quoteExpiryDate(
      workspace.selectedVersion.quoteExpiresAt ?? null,
    ),
    formalQuoteExpiresAt: workspace.selectedVersion.quoteExpiresAt,
    formalVersionId: workspace.selectedVersion.id,
    formalVersionResourceVersion: workspace.selectedVersion.resourceVersion,
    containerInstances: workspace.selectedVersion.containerInstances,
    containerEditMode:
      workspace.selectedVersion.containerEditMode ?? 'legacy_unbind',
    formalArchivedAt: workspace.archivedAt ?? undefined,
    customer: {
      ...(selectedCustomerId ? { customerId: selectedCustomerId } : {}),
      name:
        workspace.customerName ??
        (workspace.customerName === undefined
          ? (workspace.selectedVersion.pendingCustomerName ?? '')
          : ''),
      customerNo: '',
      contact: '',
      phone: '',
    },
    businessModeCode,
    businessModeLabelSnapshot:
      businessModeCode === 'rental'
        ? '免押租赁（年签月付）'
        : businessModeCode === 'short_rental'
          ? '免押租赁（短租租金）'
          : businessModeCode === 'buyout'
            ? '设备购买'
            : '空白（默认）',
    businessModeRequiresReselection: false,
    marketingDescription:
      typeof workspace.selectedVersion.frozenSnapshot?.sourceSnapshot
        .marketingDescription === 'string'
        ? workspace.selectedVersion.frozenSnapshot.sourceSnapshot
            .marketingDescription
        : undefined,
    marketingDescriptionEdited:
      workspace.selectedVersion.frozenSnapshot?.sourceSnapshot
        .marketingDescriptionEdited === true
        ? true
        : workspace.selectedVersion.frozenSnapshot?.sourceSnapshot
              .marketingDescriptionEdited === false
          ? false
          : undefined,
    outputTermsDisplayOverrides: readOutputTermsDisplayOverrides(
      workspace.selectedVersion.frozenSnapshot?.sourceSnapshot,
    ),
    outputTermsRequired:
      workspace.selectedVersion.outputTermsRequired ??
      (typeof workspace.selectedVersion.frozenSnapshot?.sourceSnapshot
        .outputTermsRequired === 'boolean'
        ? workspace.selectedVersion.frozenSnapshot.sourceSnapshot
            .outputTermsRequired
        : previousDraft?.outputTermsRequired),
    overallDiscount: Number(workspace.calculationSnapshot.discountPercent),
    modeOverallDiscounts: readQuoteModeDiscounts(
      workspace.calculationSnapshot.modeOverallDiscounts,
    ),
    items: workspace.items.map((item) => {
      const projected = projectWorkspaceItem(item, businessModeCode);
      const group = (workspace.groups ?? []).find(
        (candidate) => candidate.id === projected.quoteGroupId,
      );
      const childContainerId =
        item.containerInstanceId ?? group?.containerInstanceId;
      const childContainer = childContainerId
        ? containerInstancesById.get(childContainerId)
        : undefined;
      if (
        childContainer?.kind === 'package' &&
        childContainer.originKind === 'quote-local-package'
      ) {
        const perSet = projected.quantity / childContainer.quantity;
        if (
          !Number.isSafeInteger(childContainer.quantity) ||
          childContainer.quantity < 1 ||
          !Number.isSafeInteger(perSet) ||
          perSet < 1
        )
          throw new Error('成员数量与套餐份数不一致');
        if (
          projected.packageRelationQuantitySnapshot !== undefined &&
          projected.packageRelationQuantitySnapshot !== perSet
        )
          throw new Error('临时套餐成员数量与套餐份数不一致');
        // 兼容旧workspace DTO：先验证旧数量快照，再去掉目录关系字段。
        projected.packageRelationQuantitySnapshot = undefined;
      }
      if (
        childContainer?.kind === 'package' &&
        childContainer.originKind !== 'quote-local-package' &&
        childContainer.status === 'customized' &&
        projected.sourceFragment?.mode === 'quote-customized'
      ) {
        const perSet = projected.quantity / childContainer.quantity;
        if (!Number.isSafeInteger(perSet) || perSet < 1)
          throw new Error('报价内定制套餐成员数量与套餐份数不一致');
        // 公司套餐关系保留原每套数量；本地套餐行不携带目录关系字段。
        // 定制行可以从准确报价实例恢复数量，本地来源仍由冻结定义单独校验。
        projected.packageRelationQuantitySnapshot = perSet;
      }
      const editedContainerPlan = Boolean(
        (group?.planInstanceId &&
          customizedPlanRoots.has(group.planInstanceId)) ||
          (childContainer?.parentInstanceId &&
            customizedPlanRoots.has(childContainer.parentInstanceId)),
      );
      const instance =
        group?.planInstanceId && !editedContainerPlan
          ? (workspace.localPlanInstances ?? []).find(
              (candidate) =>
                candidate.id === group.planInstanceId &&
                candidate.status === 'active' &&
                candidate.members.some(
                  (member) => member.planMemberId === group.planMemberId,
                ),
            )
          : undefined;
      if (!instance || !group?.planMemberId) return projected;
      const member = instance.members.find(
        (candidate) => candidate.planMemberId === group.planMemberId,
      );
      const original = projected.originalDeviceIdentity;
      const equipmentMember = member?.equipmentMembers.find(
        (candidate) =>
          candidate.deviceId === (original?.deviceId ?? projected.deviceId) &&
          candidate.deviceRevisionId ===
            (original?.deviceRevisionId ??
              projected.deviceFieldSnapshot?.deviceRevisionId) &&
          candidate.packageRelationId === (projected.packageRelationId ?? null),
      );
      if (!member || !equipmentMember)
        throw new Error('报价内方案行缺少精确冻结成员');
      const packagePrice = equipmentMember.packageItemPriceCents;
      if (packagePrice === null) {
        // A null package price is an opaque/hidden source fact. The quote line
        // still carries its own frozen price, so preserve that line and the
        // group/instance snapshots without deriving a package price from it.
        return isMaskedLocalPlanPriceContextVersion(
          equipmentMember.priceContextVersion,
        )
          ? {
              ...projected,
              localPlanInstanceId: instance.id,
              localPlanMemberId: group.planMemberId,
            }
          : projected;
      }
      const localPricing = projected.deviceFieldSnapshot?.quoteLocalPricing;
      return {
        ...projected,
        localPlanInstanceId: instance.id,
        localPlanMemberId: group.planMemberId,
        packageItemPriceSnapshot: yuan(packagePrice),
        planDiscountSnapshot: instance.discountPercentSnapshot,
        planProductPriceSnapshot: planProductPrice(
          yuan(packagePrice),
          instance.discountPercentSnapshot,
        ),
        planProductSubtotalSnapshot: planProductSubtotal(
          yuan(packagePrice),
          instance.discountPercentSnapshot,
          projected.quantity,
        ),
        ...(projected.deviceFieldSnapshot &&
        localPricing?.exclusiveLineCents !== undefined
          ? {
              deviceFieldSnapshot: {
                ...projected.deviceFieldSnapshot,
                quoteLocalPricing: {
                  ...localPricing,
                  exclusiveLineBasisCents: localPricing.exclusiveLineCents,
                  exclusiveLineBasisQuantity:
                    instance.quantity * member.quantityPerSet,
                },
              },
            }
          : {}),
      };
    }),
    quoteGroups: (workspace.groups ?? []).map((group) => ({ ...group })),
    formalColumnSnapshot: workspace.columns.map((column) => ({ ...column })),
    localPackageDefinitions: (workspace.localPackageDefinitions ?? []).map(
      (definition) => ({
        ...definition,
        members: definition.members.map((member) => ({ ...member })),
      }),
    ),
    localPlanDefinitions: workspace.localPlanDefinitions ?? [],
    localPlanInstances: workspace.localPlanInstances ?? [],
    status: workspace.masterLocked
      ? workspace.businessStatus === 'ORDERED'
        ? 'success'
        : 'pending_submission'
      : orderQuoteState === 'submitted'
        ? 'success'
        : orderQuoteState === 'pending_submission'
          ? 'pending_submission'
          : workspace.selectedVersion.lifecycle === 'draft'
            ? 'draft'
            : 'customer_confirmation',
    dirty: false,
    createdAt: workspace.createdAt,
    updatedAt: workspace.updatedAt,
    versionWorkflow: undefined,
  };
  const alignedQuote = workspace.masterLocked
    ? quote
    : alignDraftPlanPricingWithCatalog(quote, plans);
  // Expose the saved baseline before a newer dirty draft is retained below.
  // Callers can fingerprint it without projecting every frozen line twice.
  onProjectedQuote?.(alignedQuote);
  return {
    ...current,
    activeView: 'workspace',
    activeQuoteId: workspace.id,
    openQuoteIds: formalQuoteTabs(current, workspace.id),
    quotes: {
      ...Object.fromEntries(
        Object.entries(current.quotes).filter(
          ([id]) =>
            /^[1-9][0-9]{0,18}$/.test(id) ||
            (isFormalNewQuoteId(id) && current.openQuoteIds.includes(id)),
        ),
      ),
      [workspace.id]:
        current.quotes[workspace.id]?.dirty &&
        current.quotes[workspace.id]?.formalVersionId ===
          workspace.selectedVersion.id
          ? current.quotes[workspace.id].formalArchivedAt ===
            (workspace.archivedAt ?? undefined)
            ? current.quotes[workspace.id]
            : {
                ...current.quotes[workspace.id],
                formalArchivedAt: workspace.archivedAt ?? undefined,
              }
          : alignedQuote,
    },
    catalog: {
      ...current.catalog,
      customers,
      devices,
      packages,
      plans,
    },
  };
}

/** A late server read must not replace unsaved rows from another selected version. */
export function workspaceReadbackConflictsWithLocalDraft(
  current: CpqState,
  workspace: QuoteWorkspaceModel,
): boolean {
  const local = current.quotes[workspace.id];
  return Boolean(
    local?.dirty &&
      local.formalVersionId &&
      local.formalVersionId !== workspace.selectedVersion.id,
  );
}

/**
 * 先把新建的正式报价放入标签集合，同时保留当前报价为活动页。
 * 路由切换随后一次完成，避免创建期间整页进入空白加载态。
 */
export function stageFormalWorkspaceTab(
  current: CpqState,
  workspace: QuoteWorkspaceModel,
): CpqState {
  const activeQuoteId = current.activeQuoteId;
  const activeQuoteVersionId = current.activeQuoteVersionId;
  const activeView = current.activeView;
  const projected = projectWorkspaceState(
    current,
    workspace,
    current.catalog.customers,
    current.catalog.devices,
    current.catalog.packages,
    current.catalog.plans,
  );
  return {
    ...projected,
    activeView,
    activeQuoteId,
    activeQuoteVersionId,
    openQuoteIds: formalQuoteTabs(current, workspace.id),
  };
}

export function projectBlankWorkspaceState(
  current: CpqState,
  customers: CustomerCatalogRecord[],
  devices: DeviceCatalogRecord[],
  packages: PackageCatalogRecord[],
  plans: PlanCatalogRecord[],
  preserveTransientDraft = true,
  blankQuoteId = FORMAL_NEW_QUOTE_ID,
): CpqState {
  const template = Object.values(current.quotes)[0];
  if (!template) throw new Error('报价页面模板不可用');
  const quote: Quote = {
    ...template,
    quoteId: blankQuoteId,
    localDraftGeneration: randomUuid(),
    masterId: undefined,
    version: 1,
    title: '未发布草稿',
    validUntil: '',
    formalQuoteExpiresAt: null,
    formalVersionId: undefined,
    formalVersionResourceVersion: undefined,
    containerEditMode: 'legacy_unbind',
    containerInstances: [],
    formalArchivedAt: undefined,
    customer: {
      customerId: '',
      name: '',
      customerNo: '',
      contact: '',
      phone: '',
    },
    businessModeCode: 'rental',
    businessModeLabelSnapshot: '免押租赁（年签月付）',
    businessModeRequiresReselection: false,
    outputTermsRequired: false,
    overallDiscount: defaultQuoteDiscountForBusinessMode('rental'),
    marketingDescription: undefined,
    marketingDescriptionEdited: undefined,
    outputTermsDisplayOverrides: undefined,
    items: [],
    quoteGroups: [],
    localPackageDefinitions: [],
    localPlanDefinitions: [],
    localPlanInstances: [],
    status: 'draft',
    dirty: false,
    salesOrderId: undefined,
    versionWorkflow: undefined,
  };
  return {
    ...current,
    activeView: 'workspace',
    activeQuoteId: blankQuoteId,
    openQuoteIds: formalQuoteTabs(current, blankQuoteId),
    quotes: {
      ...Object.fromEntries(
        Object.entries(current.quotes).filter(
          ([id]) =>
            /^[1-9][0-9]{0,18}$/.test(id) ||
            (isFormalNewQuoteId(id) && current.openQuoteIds.includes(id)),
        ),
      ),
      [blankQuoteId]:
        (preserveTransientDraft ||
          current.openQuoteIds.includes(blankQuoteId)) &&
        current.quotes[blankQuoteId]
          ? current.quotes[blankQuoteId]
          : quote,
    },
    catalog: {
      ...current.catalog,
      customers,
      devices,
      packages,
      plans,
    },
  };
}

export function projectManagementQuote(
  template: Quote,
  record: QuoteManagementRecordModel,
): Quote {
  return {
    ...template,
    quoteId: record.id,
    masterId: record.id,
    version: Number(record.currentPublishedVersion?.quoteVersionNo ?? 1),
    title: record.quoteNo,
    marketingDescription: undefined,
    customer: {
      ...(record.customerId ? { customerId: record.customerId } : {}),
      managerUserId: record.customerManagerUserId ?? undefined,
      name: record.customerName ?? '未绑定客户',
      customerNo: '',
      contact: '',
      phone: '',
    },
    items: [],
    status: record.state,
    dirty: false,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
    salesOrderId: record.salesOrderId ?? undefined,
    versionWorkflow: undefined,
  };
}

export function draftItemFromLegacy(
  item: QuoteItem,
  state: CpqState,
  sortOrder: number,
): QuoteWorkspaceDraftItem {
  const sourceType =
    item.sourceType === 'device' ? 'equipment' : item.sourceType;
  if (!['equipment', 'package', 'plan'].includes(sourceType))
    throw new Error(`来源 ${item.sourceType} 暂未接入真实报价保存`);
  const device = state.catalog.devices.find(
    (entry) => entry.id === item.deviceId,
  );
  const pkg = state.catalog.packages.find(
    (entry) =>
      entry.id === (sourceType === 'package' ? item.sourceId : item.packageId),
  );
  const plan = state.catalog.plans.find(
    (entry) =>
      entry.id === (sourceType === 'plan' ? item.sourceId : item.planId),
  );
  const matchingPlanPackages = plan?.formalPackages?.filter(
    (entry) =>
      entry.packageId === item.packageId &&
      (!item.planPackageMembershipId ||
        entry.planPackageMembershipId === item.planPackageMembershipId) &&
      (!item.packageRevisionId ||
        entry.packageRevisionId === item.packageRevisionId),
  );
  const authoritativePlanRelation = (
    matchingPlanPackages?.length === 1 ? matchingPlanPackages[0] : undefined
  )?.items.find(
    (entry) =>
      entry.packageRelationId === item.packageRelationId &&
      entry.deviceId === item.deviceId,
  );
  const authoritativePlanUnitCents =
    authoritativePlanRelation &&
    authoritativePlanRelation.lineMonthlySubtotalCents !== null &&
    authoritativePlanRelation.quantity === item.quantity &&
    authoritativePlanRelation.quantity ===
      item.packageRelationQuantitySnapshot &&
    authoritativePlanRelation.packageItemMonthlyPriceCents ===
      Math.round((item.packageItemPriceSnapshot ?? Number.NaN) * 100)
      ? Math.floor(
          (authoritativePlanRelation.lineMonthlySubtotalCents +
            Math.floor(authoritativePlanRelation.quantity / 2)) /
            authoritativePlanRelation.quantity,
        )
      : undefined;
  const sourceRevisionId =
    item.sourceRevisionId ??
    (sourceType === 'equipment'
      ? device?.currentRevisionId
      : sourceType === 'package'
        ? pkg?.currentRevisionId
        : (
            plan as
              | (PlanCatalogRecord & { currentRevisionId?: string })
              | undefined
          )?.currentRevisionId);
  if (!sourceRevisionId)
    throw new Error(`${item.sourceName} 缺少真实 revision`);
  const sourceCode =
    (sourceType === 'equipment'
      ? (item.deviceFieldSnapshot?.equipmentCode ?? device?.equipmentCode)
      : undefined) ??
    item.sourceCode ??
    (sourceType === 'package'
      ? pkg?.code
      : (plan as (PlanCatalogRecord & { code?: string }) | undefined)?.code);
  if (!sourceCode) throw new Error(`${item.sourceName} 缺少真实编码`);
  const itemId = /^[1-9][0-9]{0,18}$/.test(item.itemId)
    ? item.itemId
    : String(BigInt(Date.now()) * 1000n + BigInt(sortOrder + 1));
  const safeDeviceSnapshot = { ...(item.deviceFieldSnapshot ?? {}) };
  if (
    item.originalDevicePriceSnapshot &&
    item.originalDevicePriceSnapshot.deviceRevisionId ===
      item.originalDeviceIdentity?.deviceRevisionId
  ) {
    const { deviceRevisionId: _revision, ...prices } =
      item.originalDevicePriceSnapshot;
    Object.assign(safeDeviceSnapshot, prices);
  }
  // Effective replacement cost is a display fact, never the frozen source cost sent back on save.
  if (
    item.originalDeviceCostSnapshot &&
    item.originalDeviceCostSnapshot.deviceRevisionId ===
      item.originalDeviceIdentity?.deviceRevisionId
  )
    safeDeviceSnapshot.dynamicCost =
      item.originalDeviceCostSnapshot.dynamicCost;
  if (item.originalDeviceIdentity?.deviceId === item.deviceId) {
    const identity = item.originalDeviceIdentity;
    Object.assign(safeDeviceSnapshot, {
      name: identity.name,
      sku: identity.sku,
      jkyCode: identity.jkyCode,
      deviceRevisionId: identity.deviceRevisionId,
    });
  } else if (item.originalDeviceIdentity === null && item.replacementDeviceId) {
    // An effective target is never evidence for an unresolved frozen source.
    delete safeDeviceSnapshot.name;
    delete safeDeviceSnapshot.sku;
    delete (safeDeviceSnapshot as Record<string, unknown>).jkyCode;
    delete safeDeviceSnapshot.deviceRevisionId;
  }
  delete safeDeviceSnapshot.image;
  // 明确的未知值保留 null；没有授权读值或非数值占位不伪造为金额。
  if (
    safeDeviceSnapshot.dynamicCost !== null &&
    !Number.isFinite(safeDeviceSnapshot.dynamicCost)
  )
    delete (safeDeviceSnapshot as Record<string, unknown>).dynamicCost;
  return {
    id: itemId,
    sourceType,
    sourceId: item.sourceId,
    sourceRevisionId,
    deviceId: item.deviceId,
    sourceSnapshot: {
      name: item.sourceName,
      code: sourceCode,
      ...(item.sourceFragment
        ? {
            sourceFragment: {
              ...structuredClone(item.sourceFragment),
              ...(item.sourceFragment.mode === 'quote-customized'
                ? { fragmentKey: `quote-row:${itemId}` }
                : {}),
            },
          }
        : {}),
      ...(item.replacementPoolId && item.sourceType !== 'device'
        ? { replacementPoolId: item.replacementPoolId }
        : {}),
      ...(item.planPackageMembershipId
        ? { planPackageMembershipId: item.planPackageMembershipId }
        : {}),
      ...(item.packageNameSnapshot
        ? { packageNameSnapshot: item.packageNameSnapshot }
        : {}),
      ...(item.packageOriginPlanId && item.packageOriginPlanRevisionId
        ? {
            packageOrigin: {
              planId: item.packageOriginPlanId,
              planRevisionId: item.packageOriginPlanRevisionId,
            },
          }
        : {}),
      ...(item.sourceType === 'plan' && item.packageId
        ? {
            packageRevisionId:
              item.packageRevisionId ??
              (matchingPlanPackages?.length === 1
                ? matchingPlanPackages[0]?.packageRevisionId
                : undefined),
          }
        : {}),
      ...(item.formalPlanOccurrence
        ? {
            planOccurrence: {
              ...item.formalPlanOccurrence,
              sourceLineCents:
                item.sourceFragment &&
                usesQuoteFragmentAllocation(item.sourceFragment)
                  ? item.fragmentAllocation?.wholeRentalLineCents
                  : item.sourceFragment?.mode === 'quote-customized'
                    ? item.formalPlanOccurrence.sourceLineCents
                    : occurrenceSourceLineCents(item),
            },
          }
        : {}),
    },
    deviceFieldSnapshot: safeDeviceSnapshot,
    packageRelationId: item.formalPlanOccurrence
      ? null
      : (item.packageRelationId ?? null),
    packageId: item.packageId ?? null,
    packageItemPriceSnapshotCents:
      item.packageItemPriceSnapshot === undefined
        ? null
        : Math.round(item.packageItemPriceSnapshot * 100),
    packageRelationQuantitySnapshot:
      item.packageRelationQuantitySnapshot ?? null,
    planId: item.planId ?? null,
    planDiscountSnapshot:
      item.planDiscountSnapshot === undefined
        ? null
        : item.planDiscountSnapshot.toFixed(2),
    planProductPriceSnapshotCents:
      authoritativePlanUnitCents !== undefined
        ? authoritativePlanUnitCents
        : item.planProductPriceSnapshot === undefined
          ? null
          : Math.round(item.planProductPriceSnapshot * 100),
    // 已有草稿回切冻结来源设备时保留其余编辑，只撤销平替成员选择。
    replacementRelationId:
      item.replacementDeviceId === item.deviceId
        ? null
        : (item.replacementRelationId ?? null),
    quantity: item.quantity,
    discountPercent: item.discount.toFixed(2),
    sortOrder,
    remark: item.remark ?? null,
    groupId: item.quoteGroupId ?? null,
    ...(item.containerInstanceId === undefined
      ? {}
      : { containerInstanceId: item.containerInstanceId }),
    ...(item.exclusivePricingEdit
      ? { exclusivePricingEdit: item.exclusivePricingEdit }
      : {}),
    ...(item.exclusivePricingEditContext
      ? {
          exclusivePricingEditContext: {
            itemIds: [...item.exclusivePricingEditContext.itemIds],
            totalLineCents: item.exclusivePricingEditContext.totalLineCents,
          },
        }
      : {}),
    unitPriceCents: null,
    lineTotalCents: null,
  } as QuoteWorkspaceDraftItem;
}

export function draftContentFromLegacy(
  quote: Quote,
  state: CpqState,
): {
  items: QuoteWorkspaceDraftItem[];
  groups: QuoteGroupModel[];
} {
  const items = quote.items.map((item, index) =>
    draftItemFromLegacy(item, state, index),
  );
  const localPlanInstances = quote.localPlanInstances ?? [];
  const containersById = new Map(
    (quote.containerInstances ?? []).map((container) => [
      container.id,
      container,
    ]),
  );
  const customizedLocalPlanRoots = customizedLocalPlanRootIds(
    localPlanInstances,
    quote.containerInstances,
  );
  const isCustomizedPlanItem = (item: QuoteItem): boolean => {
    if (
      item.localPlanInstanceId &&
      customizedLocalPlanRoots.has(item.localPlanInstanceId)
    )
      return true;
    const group = quote.quoteGroups?.find(
      (candidate) => candidate.id === item.quoteGroupId,
    );
    const childContainerId =
      item.containerInstanceId ?? group?.containerInstanceId;
    const child = childContainerId
      ? containersById.get(childContainerId)
      : undefined;
    return Boolean(
      child?.parentInstanceId &&
        customizedLocalPlanRoots.has(child.parentInstanceId),
    );
  };
  const groups: QuoteGroupModel[] = [];
  const groupIds = new Map<string, string>();

  quote.items.forEach((item, index) => {
    if (!item.packageId && !item.quoteGroupId) return;
    const key = quoteTableGroupKey(item);
    let groupId = groupIds.get(key);
    if (!groupId) {
      if (
        item.localPlanInstanceId &&
        (!item.quoteGroupId || !/^[1-9][0-9]{0,18}$/.test(item.quoteGroupId))
      )
        throw new Error('报价内方案分组必须具有稳定业务标识');
      groupId =
        item.quoteGroupId && /^[1-9][0-9]{0,18}$/.test(item.quoteGroupId)
          ? item.quoteGroupId
          : items[index]?.id;
      if (!groupId) throw new Error('报价套餐分组缺少稳定行标识');
      const packageRevisionId =
        item.formalPlanOccurrence?.packageRevisionId ??
        item.packageRevisionId ??
        (item.sourceType === 'package' ? item.sourceRevisionId : undefined);
      const packageName = packageRevisionId
        ? state.catalog.packages.find(
            (candidate) =>
              candidate.id === item.packageId &&
              candidate.currentRevisionId === packageRevisionId,
          )?.name
        : undefined;
      const persistedGroup = quote.quoteGroups?.find(
        (candidate) => candidate.id === groupId,
      );
      const groupContainerId =
        item.containerInstanceId ?? persistedGroup?.containerInstanceId;
      const groupContainer = groupContainerId
        ? containersById.get(groupContainerId)
        : undefined;
      const isCatalogPackageContainer =
        groupContainer?.kind === 'package' &&
        groupContainer.originKind === 'catalog-package';
      const localDefinition = quote.localPackageDefinitions?.find(
        (definition) =>
          definition.id === persistedGroup?.definitionId ||
          definition.id === groupId,
      );
      const isQuoteLocalPackageContainer =
        groupContainer?.kind === 'package' &&
        groupContainer.originKind === 'quote-local-package' &&
        (!persistedGroup?.definitionId ||
          persistedGroup.definitionId === groupContainer.originId);
      const isQuoteLocalPackage =
        !isCatalogPackageContainer &&
        (isQuoteLocalPackageContainer ||
          Boolean(localDefinition) ||
          (persistedGroup?.kind === 'quote-local-package' &&
            (Boolean(persistedGroup.definitionId) ||
              Array.isArray(persistedGroup.members))));
      const groupProjection = isCatalogPackageContainer
        ? (() => {
            const {
              definitionId: _definitionId,
              members: _members,
              ...catalogGroup
            } = persistedGroup ?? {};
            return catalogGroup;
          })()
        : persistedGroup;
      if (
        item.localPlanInstanceId &&
        !isCustomizedPlanItem(item) &&
        (!persistedGroup ||
          persistedGroup.planInstanceId !== item.localPlanInstanceId ||
          persistedGroup.planMemberId !== item.localPlanMemberId)
      )
        throw new Error('报价内方案行与活动分组不一致');
      groups.push({
        ...groupProjection,
        id: groupId,
        ...(groupContainerId ? { containerInstanceId: groupContainerId } : {}),
        name:
          persistedGroup?.name ??
          (item.quoteGroupId && !item.packageId
            ? '报价内套餐'
            : (item.packageNameSnapshot ??
              (item.sourceType === 'package' ? item.sourceName : undefined) ??
              packageName ??
              '套餐分组')),
        sortOrder: groups.length,
        ...(isCatalogPackageContainer
          ? { kind: 'display' as const }
          : isQuoteLocalPackage
            ? {
                kind: 'quote-local-package' as const,
                ...(localDefinition
                  ? { definitionId: localDefinition.id }
                  : {}),
              }
            : persistedGroup?.kind === 'quote-local-package'
              ? { kind: 'display' as const }
              : {}),
      });
      groupIds.set(key, groupId);
    }
    const draft = items[index];
    if (draft) draft.groupId = groupId;
  });

  return { items, groups };
}
